import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  type CollectionReference,
  type DocumentData,
  type Firestore,
  type Query,
  type QueryConstraint,
  type WithFieldValue,
} from 'firebase/firestore'
import { ZodError, type ZodTypeAny } from 'zod'
import { toFirestoreData } from './converters'
import type { Logger } from './logger'

/**
 * A Firestore `onSnapshot` that surfaces `permission-denied` does NOT auto-retry
 * — the listener dies terminally. During a preview-session redeem the client can
 * briefly hold the pre-claim token, so a listener that subscribes in that window
 * (dashboard bookings/courts/activity_log) gets denied and never recovers, even
 * once the custom-token claim propagates a beat later. `subscribeList` re-subscribes
 * on denial with bounded backoff so the stream heals itself instead of leaving a
 * prospect staring at an empty demo. Other error codes pass straight through, and a
 * genuine denial for a real member resolves after the cap, then surfaces normally.
 */
function isPermissionDenied(err: unknown): boolean {
  return (err as { code?: string } | null)?.code === 'permission-denied'
}
const SNAPSHOT_DENIED_MAX_RETRIES = 8
const snapshotRetryDelayMs = (attempt: number) => Math.min(300 * 2 ** attempt, 3000)

// The shared base is bound to a single app's `db` + `logger` once, at startup,
// via `configureFirestore`. This keeps the generic `FirestoreRepository` a
// normal module-level export (so domain repos in either app can
// `extends FirestoreRepository<typeof Schema>` with an unchanged
// `super(path, schema)` constructor) while still injecting the app-specific
// Firestore instance — a factory that *returned* the class would make the
// inferred type un-nameable across module boundaries (TS4023).
let boundDb: Firestore | null = null
let boundLogger: Logger | null = null

export interface FirestoreRuntime {
  db: Firestore
  logger: Logger
}

/**
 * Bind the shared Firestore base to this app's `db` + `logger`. Call once at
 * startup (each app does this from its own `core/firebase` layer) BEFORE any
 * repository method runs. Constructors only store path+schema, so binding
 * before the first read/write/subscribe is sufficient.
 */
export function configureFirestore(runtime: FirestoreRuntime): void {
  boundDb = runtime.db
  boundLogger = runtime.logger
}

function db(): Firestore {
  if (!boundDb) {
    throw new Error(
      '@upadelmatch/core: configureFirestore({ db, logger }) must be called before using FirestoreRepository.',
    )
  }
  return boundDb
}

function log(): Logger {
  if (!boundLogger) {
    throw new Error(
      '@upadelmatch/core: configureFirestore({ db, logger }) must be called before using FirestoreRepository.',
    )
  }
  return boundLogger
}

/**
 * Reusable Firestore CRUD helpers built around a Zod schema.
 *
 * Every read passes through `schema.parse({ id, ...data })`, so callers always
 * receive a validated, typed object. Validation failures are logged with the
 * doc path so the bad document is identifiable in production.
 */
export class FirestoreRepository<TSchema extends ZodTypeAny> {
  constructor(
    /** Path to the collection, e.g. `clubs/{clubId}/courts`. */
    protected readonly path: string,
    /** Zod schema for documents in this collection. Must include `id: z.string()`. */
    protected readonly schema: TSchema,
  ) {}

  protected get collectionRef(): CollectionReference<DocumentData> {
    return collection(db(), this.path)
  }

  protected docRef(id: string) {
    return doc(db(), this.path, id)
  }

  /** Validate a Firestore snapshot via the schema. Returns null on validation failure. */
  protected parse(id: string, data: DocumentData | undefined): TSchema['_output'] | null {
    if (!data) return null
    try {
      return this.schema.parse({ id, ...data })
    } catch (e) {
      if (e instanceof ZodError) {
        log().e(`schema validation failed for ${this.path}/${id}`, e.issues)
      } else {
        log().e(`unexpected parse error for ${this.path}/${id}`, e)
      }
      return null
    }
  }

  async getById(id: string): Promise<TSchema['_output'] | null> {
    const snap = await getDoc(this.docRef(id))
    if (!snap.exists()) return null
    return this.parse(snap.id, snap.data())
  }

  async list(...constraints: QueryConstraint[]): Promise<TSchema['_output'][]> {
    const q: Query<DocumentData> =
      constraints.length === 0
        ? this.collectionRef
        : (await import('firebase/firestore')).query(this.collectionRef, ...constraints)
    const snap = await getDocs(q)
    return snap.docs
      .map((d) => this.parse(d.id, d.data()))
      .filter((v): v is TSchema['_output'] => v !== null)
  }

  /**
   * Live variant of `list` — an `onSnapshot` stream over the same constraints,
   * with the same per-doc `schema.parse` (invalid docs dropped + logged).
   * Returns the Firestore unsubscribe. Use behind `sharedSubscribe` so the
   * read surface auto-updates the instant a Cloud Function projects/rolls up
   * new data (no refetch race), and across devices.
   */
  subscribeList(
    constraints: QueryConstraint[],
    onNext: (rows: TSchema['_output'][]) => void,
    onError: (err: Error) => void,
  ): () => void {
    const q: Query<DocumentData> =
      constraints.length === 0 ? this.collectionRef : query(this.collectionRef, ...constraints)

    // Self-healing subscription: a `permission-denied` from the preview-redeem
    // auth race re-subscribes (bounded) so the stream recovers once the claim
    // propagates, instead of dying terminally. See isPermissionDenied above.
    let unsubscribe: (() => void) | null = null
    let retryTimer: ReturnType<typeof setTimeout> | null = null
    let deniedRetries = 0
    let cancelled = false

    const subscribe = () => {
      unsubscribe = onSnapshot(
        q,
        (snap) => {
          deniedRetries = 0 // a successful snapshot proves the claim is live
          onNext(
            snap.docs
              .map((d) => this.parse(d.id, d.data()))
              .filter((v): v is TSchema['_output'] => v !== null),
          )
        },
        (err) => {
          if (isPermissionDenied(err) && !cancelled && deniedRetries < SNAPSHOT_DENIED_MAX_RETRIES) {
            const delay = snapshotRetryDelayMs(deniedRetries)
            deniedRetries += 1
            // Debug-only: an expected, self-healing event during the redeem race —
            // not a Sentry-worthy incident. If retries exhaust, the caller's own
            // onError (logger.w) surfaces the genuine failure.
            log().d(
              `${this.path} snapshot permission-denied — re-subscribing (attempt ${deniedRetries}/${SNAPSHOT_DENIED_MAX_RETRIES}, ${delay}ms)`,
            )
            unsubscribe?.()
            unsubscribe = null
            retryTimer = setTimeout(() => {
              retryTimer = null
              if (!cancelled) subscribe()
            }, delay)
            return
          }
          onError(err)
        },
      )
    }

    subscribe()

    return () => {
      cancelled = true
      if (retryTimer) clearTimeout(retryTimer)
      unsubscribe?.()
    }
  }

  /** Create or replace a document. `id` is preserved; `createdAt`/`updatedAt` are set server-side. */
  async set(
    id: string,
    payload: WithFieldValue<DocumentData>,
    options: { setCreatedAt?: boolean } = {},
  ): Promise<void> {
    const data = toFirestoreData(payload as Record<string, unknown>)
    data['updatedAt'] = serverTimestamp()
    if (options.setCreatedAt) data['createdAt'] = serverTimestamp()
    await setDoc(this.docRef(id), data, { merge: true })
  }

  async update(id: string, partial: Partial<TSchema['_output']>): Promise<void> {
    const data = toFirestoreData(partial as Record<string, unknown>)
    data['updatedAt'] = serverTimestamp()
    await updateDoc(this.docRef(id), data)
  }

  async remove(id: string): Promise<void> {
    await deleteDoc(this.docRef(id))
  }
}
